import React, { useState, useEffect } from 'react';
import { Search, Phone, MessageCircle, Mail, Calendar, CheckCircle, Clock, Eye, ArrowRight } from 'lucide-react';

interface ActionStage {
  id: number;
  icon: React.ReactNode;
  title: string;
  agent: string;
  description: string;
  status: string;
  statusIcon: React.ReactNode;
  gradient: string;
  iconBg: string;
}

const AIFunnelVisualization: React.FC = () => {
  const [currentStage, setCurrentStage] = useState(0);

  const stages: ActionStage[] = [
    {
      id: 1,
      icon: <Search className="w-4 h-4 text-white" />,
      title: "Lead Discovery",
      agent: "Lead Gen Agent",
      description: "Found: John Smith, CEO at TechCorp",
      status: "Contact verified",
      statusIcon: <CheckCircle className="w-4 h-4 text-brand-green-teal" />,
      gradient: "from-brand-blue to-brand-green-teal",
      iconBg: "bg-chip-bg border border-chip-border"
    },
    {
      id: 2,
      icon: <Phone className="w-4 h-4 text-white" />,
      title: "Outbound Call",
      agent: "Call Agent",
      description: "Connected and discussed pain points",
      status: "Call completed",
      statusIcon: <CheckCircle className="w-4 h-4 text-brand-green-teal" />,
      gradient: "from-brand-blue to-brand-green-teal",
      iconBg: "bg-chip-bg border border-chip-border"
    },
    {
      id: 3,
      icon: <MessageCircle className="w-4 h-4 text-white" />,
      title: "WhatsApp Follow-up",
      agent: "WhatsApp Agent",
      description: "Sent demo link and resources",
      status: "Message read",
      statusIcon: <Eye className="w-4 h-4 text-brand-blue" />,
      gradient: "from-brand-blue to-brand-green-teal",
      iconBg: "bg-chip-bg border border-chip-border"
    },
    {
      id: 4,
      icon: <Mail className="w-4 h-4 text-white" />,
      title: "Email Proposal",
      agent: "Email Agent",
      description: "Detailed proposal with ROI analysis",
      status: "Email opened",
      statusIcon: <Eye className="w-4 h-4 text-brand-blue" />,
      gradient: "from-neon-blue to-neon-pink",
      iconBg: "bg-chip-bg border border-chip-border"
    },
    {
      id: 5,
      icon: <Calendar className="w-4 h-4 text-white" />,
      title: "Meeting Booked",
      agent: "Meeting Booked",
      description: "Demo scheduled for tomorrow 2:00 PM",
      status: "Confirmed",
      statusIcon: <CheckCircle className="w-4 h-4 text-brand-green-teal" />,
      gradient: "from-neon-pink to-brand-green-teal",
      iconBg: "bg-chip-bg border border-chip-border"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStage((prev) => (prev + 1) % stages.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [stages.length]);

  const currentAction = stages[currentStage];

  return (
    <div className="relative w-full max-w-4xl mx-auto px-2 sm:px-0">
      {/* Process Flow */}
      <div className="mb-6 sm:mb-8">
        <div className="flex items-center justify-between relative">
          {stages.map((stage, index) => (
            <React.Fragment key={stage.id}>
              {/* Process Stage */}
              <div className="flex flex-col items-center relative z-10">
                {/* Agent Avatar */}
                <div 
                  className={`
                    w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 rounded-full flex items-center justify-center mb-2 sm:mb-3
                    transition-all duration-500 shadow-chip-glow relative
                    ${index === currentStage 
                      ? `${stage.iconBg} neon-ring-active animate-breathe shadow-glow-combined scale-110` 
                      : index < currentStage
                        ? `${stage.iconBg} shadow-chip-glow`
                        : 'bg-chip-bg border border-chip-border shadow-chip-glow'
                    }
                  `}
                >
                  {React.cloneElement(stage.icon as React.ReactElement, {
                    className: `w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-chip-text`
                  })}
                  
                  {/* Active Pulse Ring */}
                  {index === currentStage && (
                    <div className="absolute inset-0 rounded-full border-2 border-neon-blue/30 animate-ping" />
                  )}
                </div>

                {/* Stage Title */}
                <div className={`
                  hidden sm:block
                  text-center transition-all duration-500 max-w-12 sm:max-w-16 lg:max-w-20
                  ${index === currentStage 
                    ? 'text-text-primary font-semibold' 
                    : index < currentStage
                      ? 'text-text-secondary font-medium'
                      : 'text-text-secondary/60'
                  }
                `}>
                  <div className="text-xs leading-tight">
                    <span className="hidden sm:inline">{stage.agent}</span>
                    <span className="sm:hidden">{stage.agent.split(' ')[0]}</span>
                  </div>
                </div>

                {/* Completion Indicator */}
                {index < currentStage && (
                  <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 w-4 h-4 sm:w-6 sm:h-6 bg-brand-green-teal rounded-full flex items-center justify-center">
                    <CheckCircle className="w-2 h-2 sm:w-3 sm:h-3 text-white" />
                  </div>
                )}
              </div>

              {/* Connecting Arrow */}
              {index < stages.length - 1 && (
                <div className="flex-1 flex items-center justify-center px-1 sm:px-2">
                  <ArrowRight 
                    className={`
                      w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 transition-all duration-500
                      ${index < currentStage 
                        ? 'text-brand-green-teal' 
                        : index === currentStage
                          ? 'text-brand-blue animate-pulse'
                          : 'text-card-border'
                      }
                    `} 
                  />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Progress Line */}
        <div className="relative mt-3 sm:mt-4 h-1 bg-card-border rounded-full overflow-hidden">
          <div 
            className="absolute top-0 left-0 h-full bg-gradient-brand rounded-full transition-all duration-1000 ease-out"
            style={{ 
              width: `${((currentStage + 1) / stages.length) * 100}%` 
            }}
          />
        </div>
      </div>

      {/* Current Action Details */}
      <div className="relative">
        <div 
          className={`
            bg-bg-surface backdrop-blur-sm border border-card-border
            rounded-xl sm:rounded-2xl p-4 sm:p-6 transition-all duration-700 ease-out
            transform hover:scale-105 shadow-glow-combined
            animate-popIn
          `}
          key={currentAction.id}
        >
          {/* Header */}
          <div className="flex items-center space-x-3 sm:space-x-4 mb-3 sm:mb-4">
            <div className={`
              w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-chip-bg border border-chip-border
              flex items-center justify-center shadow-chip-glow
            `}>
              {currentAction.icon}
            </div>
            <div className="flex-1">
              <div className="text-text-primary text-base sm:text-lg font-bold">
                {currentAction.title}
              </div>
              <div className="text-text-secondary text-xs sm:text-sm">
                {currentAction.agent}
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="mb-3 sm:mb-4">
            <div className="text-text-primary text-sm sm:text-base">
              {currentAction.description}
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center space-x-2">
            {currentAction.statusIcon}
            <span className="text-text-secondary text-xs sm:text-sm font-medium">
              {currentAction.status}
            </span>
          </div>
        </div>

        {/* Subtle glow effect */}
        <div 
          className={`
            absolute inset-0 rounded-2xl opacity-20 blur-xl -z-10
            bg-gradient-to-r from-neon-blue to-neon-pink
          `}
        />
      </div>

    </div>
  );
};

export default AIFunnelVisualization;